import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { X, Search } from "lucide-react";
import "./SearchInput.css";

export interface SearchInputProps {
  /** Current value of the search input */
  value: string;
  /** Called immediately on every keystroke */
  onChange: (value: string) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Called after debounce delay with the debounced value */
  onDebouncedChange?: (value: string) => void;
  /** Icon to show on the left side of the input */
  icon?: React.ReactNode;
  /** Whether to show the search icon (convenience prop, overridden by icon) */
  showSearchIcon?: boolean;
  /** Whether to show the clear button when input has value */
  showClearButton?: boolean;
  /** Accessibility label for the input */
  ariaLabel?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to auto-focus the input on mount */
  autoFocus?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Called when Enter key is pressed */
  onEnter?: (value: string) => void;
}

/**
 * Reusable search input component with built-in debouncing,
 * optional clear button, and optional icon.
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder,
  debounceMs = 300,
  onDebouncedChange,
  icon,
  showSearchIcon = false,
  showClearButton = true,
  ariaLabel,
  disabled = false,
  autoFocus = false,
  className = "",
  onEnter,
}) => {
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = useState(value);

  // Sync internal value when external value changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Debounced change handler
  useEffect(() => {
    if (!onDebouncedChange) return;

    const timer = setTimeout(() => {
      onDebouncedChange(internalValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [internalValue, debounceMs, onDebouncedChange]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange(newValue);
  };

  const handleClear = () => {
    setInternalValue("");
    onChange("");
    // Immediately trigger debounced callback with empty string
    onDebouncedChange?.("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onEnter) {
      e.preventDefault();
      onEnter(internalValue);
    }
  };

  // Determine which icon to show
  const displayIcon = icon || (showSearchIcon ? <Search size={20} /> : null);
  const hasIcon = !!displayIcon;
  const hasValue = internalValue.length > 0;

  return (
    <div className={`search-input-container ${className}`}>
      {hasIcon && (
        <span className="search-input-icon" aria-hidden="true">
          {displayIcon}
        </span>
      )}
      <input
        ref={inputRef}
        type="text"
        className={`search-input-field ${hasIcon ? "has-icon" : ""} ${showClearButton && hasValue ? "has-clear" : ""}`}
        placeholder={placeholder ?? t("actions.search")}
        value={internalValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel ?? t("actions.search")}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {showClearButton && hasValue && (
        <button
          type="button"
          className="search-input-clear"
          onClick={handleClear}
          aria-label={t("actions.clear")}
          disabled={disabled}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
